import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Loader2, AlertCircle, Check, ArrowLeft, Mail } from 'lucide-react';

const ForgotPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { forgotPassword, resetPasswordWithToken } = useApp();

  // Token flow: /forgot-password?token=xxx OR /reset-password?token=xxx
  const params = new URLSearchParams(location.search);
  const urlToken = params.get('token') || '';

  const [mode, setMode] = useState(urlToken ? 'reset' : 'request');
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [linkSent, setLinkSent] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    setMode(urlToken ? 'reset' : 'request');
  }, [urlToken]);

  const handleRequestLink = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      const ok = await forgotPassword(email);
      if (ok) {
        setLinkSent(true);
      } else {
        setError('Something went wrong. Please try again.');
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    setError('');
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    setIsLoading(true);
    try {
      const ok = await resetPasswordWithToken(urlToken, newPassword);
      if (ok) {
        setSuccess(true);
      } else {
        setError('This reset link is no longer valid. Please request a new one.');
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-neutral-950">
        <Navbar />
        <div className="pt-32 pb-20 px-4">
          <div className="max-w-md mx-auto text-center">
            <div className="w-20 h-20 bg-green-100 dark:bg-emerald-500/15 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-10 h-10 text-green-600 dark:text-emerald-400" />
            </div>
            <h1 className="text-2xl font-black text-gray-900 dark:text-neutral-100 mb-4">Password Reset Successful!</h1>
            <p className="text-gray-600 dark:text-neutral-400 mb-8">
              Your password has been updated. You can now sign in with your new password.
            </p>
            <button
              onClick={() => navigate('/login')}
              data-testid="reset-goto-login-btn"
              className="w-full py-4 bg-black text-white rounded-xl font-bold hover:bg-gray-800 transition-colors"
            >
              Sign In
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (linkSent) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-neutral-950">
        <Navbar />
        <div className="pt-32 pb-20 px-4">
          <div className="max-w-md mx-auto text-center">
            <div className="w-20 h-20 bg-blue-100 dark:bg-blue-500/15 rounded-full flex items-center justify-center mx-auto mb-6">
              <Mail className="w-10 h-10 text-blue-600 dark:text-blue-400" />
            </div>
            <h1 className="text-2xl font-black text-gray-900 dark:text-neutral-100 mb-4">Check your inbox</h1>
            <p className="text-gray-600 dark:text-neutral-400 mb-8">
              If an account exists for that email, we've sent a password reset link. It expires in 1 hour.
            </p>
            <Link
              to="/login"
              className="inline-block w-full py-4 bg-black text-white rounded-xl font-bold hover:bg-gray-800 transition-colors"
            >
              Back to Login
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-neutral-950">
      <Navbar />

      <div className="pt-32 pb-20 px-4">
        <div className="max-w-md mx-auto">
          <Link
            to="/login"
            className="inline-flex items-center gap-2 text-gray-600 dark:text-neutral-400 hover:text-black dark:hover:text-white mb-8"
          >
            <ArrowLeft size={18} /> Back to Login
          </Link>

          <div className="text-center mb-8">
            <h1 className="text-2xl font-black text-gray-900 dark:text-neutral-100">Reset Password</h1>
            <p className="text-gray-600 dark:text-neutral-400 mt-2">
              {mode === 'request'
                ? "Enter your email and we'll send you a reset link."
                : 'Choose a new password for your account.'}
            </p>
          </div>

          <div className="bg-white dark:bg-neutral-900 rounded-3xl p-8 shadow-sm border border-gray-100 dark:border-neutral-800">
            {mode === 'request' ? (
              <form onSubmit={handleRequestLink} className="space-y-5" data-testid="forgot-request-form">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-neutral-300 mb-2">Email Address</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="forgot-email-input"
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-neutral-800 focus:outline-none focus:border-black transition-colors"
                    placeholder="you@example.com"
                  />
                </div>

                {error && (
                  <div className="flex items-center gap-2 text-red-600 dark:text-red-400 text-sm p-3 bg-red-50 dark:bg-red-500/10 rounded-xl">
                    <AlertCircle size={18} />
                    <span>{error}</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isLoading}
                  data-testid="forgot-send-link-btn"
                  className="w-full py-4 bg-black text-white rounded-xl font-bold hover:bg-gray-800 transition-colors disabled:opacity-50 flex items-center justify-center"
                >
                  {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Send Reset Link'}
                </button>
              </form>
            ) : (
              <form onSubmit={handleReset} className="space-y-5" data-testid="forgot-reset-form">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-neutral-300 mb-2">New Password</label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={6}
                    data-testid="forgot-new-password-input"
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-neutral-800 focus:outline-none focus:border-black transition-colors"
                    placeholder="••••••••"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-neutral-300 mb-2">Confirm Password</label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength={6}
                    data-testid="forgot-confirm-password-input"
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-neutral-800 focus:outline-none focus:border-black transition-colors"
                    placeholder="••••••••"
                  />
                </div>

                {error && (
                  <div className="flex items-center gap-2 text-red-600 dark:text-red-400 text-sm p-3 bg-red-50 dark:bg-red-500/10 rounded-xl">
                    <AlertCircle size={18} />
                    <span>{error}</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isLoading}
                  data-testid="forgot-submit-reset-btn"
                  className="w-full py-4 bg-black text-white rounded-xl font-bold hover:bg-gray-800 transition-colors disabled:opacity-50 flex items-center justify-center"
                >
                  {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Reset Password'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ForgotPassword;
